/**
 * PressReleasesExtractionsContext
 *
 * Provides a React context and two convenience hooks for working with
 * press release extraction datapoints in the issue form. The module defines the
 * `FieldProvenance` and `PressReleasesExtractionsContextValue` types, supplies a sensible
 * `emptyValue` default, and exports `PressReleasesExtractionsContext` plus
 * `useSuggestionDatapointsVisibility` and `usePressReleasesExtractions` hooks for consuming
 * visibility state, extracted fields, and provenance metadata from the
 * surrounding form UI.
 */
import React from 'react'

type FieldProvenance = {
    sourceUrl: string | null
    sourcePages: number[]
    extractionId: string | null
    quote: string | null
}

type PressReleasesExtractionsContextValue = {
    showSuggestionDatapoints: boolean
    setShowSuggestionDatapoints: React.Dispatch<React.SetStateAction<boolean>>
    suggestionImportantDates: Record<string, string | null>
    suggestionOfferingTerms: Record<string, number | string | null>
    suggestionOfferingOutcome: Record<string, number | string | null>
    suggestionProvenanceByField: Record<string, FieldProvenance>
}

const emptyValue: PressReleasesExtractionsContextValue = {
    showSuggestionDatapoints: false,
    setShowSuggestionDatapoints: () => undefined,
    suggestionImportantDates: {},
    suggestionOfferingTerms: {},
    suggestionOfferingOutcome: {},
    suggestionProvenanceByField: {},
}

export const PressReleasesExtractionsContext = React.createContext<PressReleasesExtractionsContextValue>(emptyValue)

export const useSuggestionDatapointsVisibility = () => {
    const context = React.useContext(PressReleasesExtractionsContext)
    return {
        showSuggestionDatapoints: context.showSuggestionDatapoints,
        setShowSuggestionDatapoints: context.setShowSuggestionDatapoints,
    }
}

export const usePressReleasesExtractions = () => {
    const context = React.useContext(PressReleasesExtractionsContext)
    return {
        suggestionImportantDates: context.suggestionImportantDates,
        suggestionOfferingTerms: context.suggestionOfferingTerms,
        suggestionOfferingOutcome: context.suggestionOfferingOutcome,
        suggestionProvenanceByField: context.suggestionProvenanceByField,
    }
}
